import React, { useState, useCallback, useMemo } from 'react';
import MacroGraph from '../../components/MacroGraph/MacroGraph';
import {
  SCENARIOS, EDGES,
  MacroNode, MacroEdge, MacroScenario,
  pathToEdgeIds, nodeById, edgeById,
} from '../../data/macroData';

const MacroPage: React.FC = () => {
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [detailContent, setDetailContent] = useState<React.ReactNode>(null);

  // Compute active edges/nodes from scenario
  const { activeEdges, activeNodes } = useMemo(() => {
    const edges = new Set<string>();
    const nodes = new Set<string>();

    if (selectedScenario) {
      const scn = SCENARIOS.find((s) => s.id === selectedScenario);
      if (scn) {
        scn.paths.forEach((p) => {
          p.forEach((n) => nodes.add(n));
          pathToEdgeIds(p).forEach((eid) => edges.add(eid));
        });
      }
    }

    return { activeEdges: edges, activeNodes: nodes };
  }, [selectedScenario]);

  const renderScenarioDetail = useCallback((scn: MacroScenario) => {
    return (
      <div>
        <h3 className="text-base font-bold text-viz-text-primary mt-1">{scn.name}</h3>
        <p className="text-xs text-viz-text-secondary mt-1 leading-relaxed">{scn.summary}</p>
        <div className="border-t border-viz-border my-3" />
        {scn.paths.map((path, idx) => (
          <div key={idx} className="mb-4">
            <div className="text-xs font-semibold text-viz-text-secondary mb-1">
              Цепочка {idx + 1}
            </div>
            <div className="text-xs text-viz-text-secondary mb-2">
              {path.map((n) => `${nodeById.get(n)?.icon || '•'} ${nodeById.get(n)?.label || n}`).join(' → ')}
            </div>
            {pathToEdgeIds(path).map((eid, i) => {
              const edge = edgeById.get(eid);
              if (!edge) return null;
              const fromNode = nodeById.get(edge.from);
              const toNode = nodeById.get(edge.to);
              return (
                <div key={eid} className="border border-viz-border rounded-lg p-3 mb-2 bg-viz-bg-primary/50">
                  <div className="text-sm font-semibold text-viz-text-primary">
                    Шаг {i + 1}: {fromNode?.icon} {fromNode?.label} → {toNode?.icon} {toNode?.label}
                    <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full border border-viz-border text-viz-text-secondary">
                      {edge.sign > 0 ? '+' : '−'}{Math.round(edge.w * 100)}%
                    </span>
                  </div>
                  <p className="text-xs text-viz-text-secondary mt-1.5 leading-relaxed">
                    <strong>Почему:</strong> {edge.why}
                  </p>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  }, []);

  const renderNodeDetail = useCallback((node: MacroNode) => {
    const connectedEdges = EDGES.filter((e) => e.from === node.id || e.to === node.id);
    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-2xl">{node.icon}</span>
          <div>
            <h3 className="text-base font-bold text-viz-text-primary">{node.label}</h3>
            <p className="text-xs text-viz-text-secondary">{node.row} / {node.subgroup}</p>
          </div>
        </div>
        <p className="text-xs text-viz-text-secondary mb-3">{node.desc}</p>
        {connectedEdges.length > 0 && (
          <>
            <div className="border-t border-viz-border my-2" />
            <p className="text-xs font-semibold text-viz-text-secondary mb-2">Связи ({connectedEdges.length}):</p>
            {connectedEdges.map((e) => {
              const from = nodeById.get(e.from);
              const to = nodeById.get(e.to);
              return (
                <div key={e.id} className="text-xs text-viz-text-secondary mb-1">
                  {from?.icon} {from?.label} → {to?.icon} {to?.label} ({e.sign > 0 ? '+' : '−'}{Math.round(e.w * 100)}%)
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  }, []);

  const renderEdgeDetail = useCallback((edge: MacroEdge) => {
    const from = nodeById.get(edge.from);
    const to = nodeById.get(edge.to);
    return (
      <div>
        <h3 className="text-base font-bold text-viz-text-primary mb-2">
          {from?.icon} {from?.label} → {to?.icon} {to?.label}
        </h3>
        <div className="flex gap-3 mb-3">
          <span className={`text-xs px-2 py-1 rounded-full ${edge.sign > 0 ? 'bg-viz-success-bg text-viz-success' : 'bg-viz-error-bg text-viz-error'}`}>
            {edge.sign > 0 ? 'Positive' : 'Negative'} ({edge.sign > 0 ? '+' : '−'}{Math.round(edge.w * 100)}%)
          </span>
        </div>
        <p className="text-xs text-viz-text-secondary leading-relaxed">
          <strong>Почему:</strong> {edge.why}
        </p>
      </div>
    );
  }, []);

  const handleScenarioChange = useCallback((id: string) => {
    setSelectedScenario(id);
    if (!id) {
      setDetailContent(null);
      return;
    }
    const scn = SCENARIOS.find((s) => s.id === id);
    if (scn) setDetailContent(renderScenarioDetail(scn));
  }, [renderScenarioDetail]);

  const handleNodeClick = useCallback((node: MacroNode) => {
    setSelectedScenario('');
    setDetailContent(renderNodeDetail(node));
  }, [renderNodeDetail]);

  const handleEdgeClick = useCallback((edge: MacroEdge) => {
    setSelectedScenario('');
    setDetailContent(renderEdgeDetail(edge));
  }, [renderEdgeDetail]);

  const handleBackgroundClick = useCallback(() => {
    setSelectedScenario('');
    setDetailContent(null);
  }, []);

  return (
    <div className="flex h-[calc(100vh-56px)]">
      {/* Left Panel */}
      <div className="w-[380px] shrink-0 glass-card m-3 mr-0 p-4 overflow-y-auto flex flex-col">
        <h2 className="text-sm font-semibold text-viz-text-primary mb-2">Событие / сценарий</h2>
        <p className="text-xs text-viz-text-secondary leading-relaxed mb-3">
          Выбери событие в списке — на графе подсветятся цепочки, а ниже появится объяснение "почему".
        </p>

        <select
          value={selectedScenario}
          onChange={(e) => handleScenarioChange(e.target.value)}
          className="w-full px-3 py-2.5 bg-viz-bg-tertiary/50 border border-viz-border rounded-lg text-sm text-viz-text-primary focus:border-viz-border-active focus:outline-none mb-3"
        >
          <option value="">— Не выбрано (показать всё) —</option>
          {SCENARIOS.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <div className="flex gap-2 mb-4">
          <button
            onClick={() => handleScenarioChange('')}
            className="px-3 py-1.5 text-xs border border-viz-border rounded-lg text-viz-text-secondary hover:bg-viz-bg-tertiary transition-colors"
          >
            Сброс
          </button>
          <button
            onClick={() => {
              const svg = document.querySelector('.macro-graph-svg') as any;
              svg?.__fitToScreen?.();
            }}
            className="px-3 py-1.5 text-xs btn-gradient rounded-lg"
          >
            Fit to screen
          </button>
        </div>

        <div className="border-t border-viz-border my-2" />

        <h2 className="text-sm font-semibold text-viz-text-primary mb-2">Логика и влияния</h2>
        <div className="flex-1 overflow-y-auto">
          {detailContent || (
            <p className="text-xs text-viz-text-secondary">
              Выбери сценарий сверху или кликни по узлу/связи на графе.
            </p>
          )}
        </div>

        <div className="border-t border-viz-border mt-3 pt-3">
          <p className="text-[10px] text-viz-text-secondary leading-relaxed">
            Управление: колесо — зум, перетаскивание — пан, узлы — drag.
          </p>
        </div>
      </div>

      {/* Graph Area */}
      <div className="flex-1 m-3">
        <MacroGraph
          activeEdges={activeEdges}
          activeNodes={activeNodes}
          onNodeClick={handleNodeClick}
          onEdgeClick={handleEdgeClick}
          onBackgroundClick={handleBackgroundClick}
        />
      </div>
    </div>
  );
};

export default MacroPage;
